/**
 * V2Hero — V2.0 Foundation primitive
 *
 * A hero-section wrapper. Supports a split layout (text left, illustration
 * right) or a centered layout (text-only, centered).
 *
 * Example:
 *   <V2Hero
 *     eyebrow="Funding readiness"
 *     title="Get funding-ready in weeks, not years."
 *     subtitle="Analyze your credit, repair blockers, and match with vetted funding partners."
 *     primaryCta={{ label: "Start free check", href: "/signup" }}
 *     secondaryCta={{ label: "How it works", href: "#how-it-works" }}
 *     illustration={<img src="/hero.png" alt="" />}
 *   />
 *
 * Test IDs:
 *   v2-hero, v2-hero-eyebrow, v2-hero-title, v2-hero-subtitle,
 *   v2-hero-primary-cta, v2-hero-secondary-cta, v2-hero-illustration
 */
import React from "react";
import clsx from "clsx";
import { ArrowRight } from "lucide-react";

function Cta({ testId, cta, variant = "primary" }) {
  if (!cta) return null;
  const { label, onClick, href, icon: Icon } = cta;
  const base =
    "inline-flex items-center justify-center gap-2 rounded-v2-md px-5 py-2.5 text-v2-body-md font-semibold transition-all";
  const styles =
    variant === "primary"
      ? "bg-v2-accent text-v2-accentInk hover:bg-v2-accentStrong shadow-v2-card hover:shadow-v2-lifted"
      : "bg-v2-surface text-v2-text border border-v2-border hover:border-v2-borderStrong hover:bg-v2-surfaceSubtle";
  const contents = (
    <>
      {Icon ? <Icon className="h-4 w-4" aria-hidden="true" /> : null}
      <span>{label}</span>
      {variant === "primary" && !Icon ? (
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      ) : null}
    </>
  );
  if (href) {
    return (
      <a data-testid={testId} href={href} onClick={onClick} className={clsx(base, styles)}>
        {contents}
      </a>
    );
  }
  return (
    <button
      data-testid={testId}
      type="button"
      onClick={onClick}
      className={clsx(base, styles)}
    >
      {contents}
    </button>
  );
}

export default function V2Hero({
  eyebrow,
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  illustration,
  layout = "split",
}) {
  const centered = layout === "centered";
  return (
    <section
      data-testid="v2-hero"
      className={clsx(
        "w-full rounded-v2-2xl bg-v2-surface border border-v2-border shadow-v2-card",
        "px-6 py-10 md:px-10 md:py-14"
      )}
    >
      <div
        className={clsx(
          "grid gap-10 items-center",
          centered ? "grid-cols-1 max-w-3xl mx-auto text-center" : "md:grid-cols-[1.15fr_1fr]"
        )}
      >
        <div>
          {eyebrow ? (
            <div
              data-testid="v2-hero-eyebrow"
              className="text-v2-caption uppercase text-v2-accentStrong mb-4"
            >
              {eyebrow}
            </div>
          ) : null}
          <h1
            data-testid="v2-hero-title"
            className="text-v2-display-md md:text-v2-display-lg text-v2-text"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
          >
            {title}
          </h1>
          {subtitle ? (
            <p
              data-testid="v2-hero-subtitle"
              className="mt-5 text-v2-body-lg text-v2-textMuted max-w-2xl"
            >
              {subtitle}
            </p>
          ) : null}
          {(primaryCta || secondaryCta) && (
            <div
              className={clsx(
                "mt-8 flex flex-col sm:flex-row gap-3",
                centered ? "justify-center" : ""
              )}
            >
              <Cta testId="v2-hero-primary-cta" cta={primaryCta} variant="primary" />
              <Cta testId="v2-hero-secondary-cta" cta={secondaryCta} variant="secondary" />
            </div>
          )}
        </div>
        {!centered && illustration ? (
          <div data-testid="v2-hero-illustration" className="w-full">
            {illustration}
          </div>
        ) : null}
      </div>
    </section>
  );
}
